import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
    AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip,
    ResponsiveContainer, CartesianGrid
} from "recharts";
import {
    Network, Activity, Globe, Server, Filter,
    Calendar, RotateCw, FileText, Printer,
    ShieldAlert, Wifi, Layers, TrendingUp, MapPin,
    ShieldCheck, AlertCircle, ChevronDown, ChevronUp, ArrowUpRight,
    Zap, Check, Copy, X, Clock, Shield, Search, Radio, SlidersHorizontal
} from "lucide-react";
import { backendURL } from "../config";

// ─── Theme & Palette Constants ─────────────────────────────────
const PROTOCOL_COLORS = [
    "#3b82f6", // Blue
    "#10b981", // Emerald
    "#8b5cf6", // Purple
    "#06b6d4", // Cyan
    "#f59e0b", // Amber
    "#ec4899", // Pink
    "#f97316", // Orange
    "#14b8a6", // Teal
    "#a855f7", // Violet
    "#ef4444", // Red
    "#6366f1", // Indigo
    "#84cc16"  // Lime
];

const riskBadgeStyle = (level) => {
    switch (level) {
        case "CRITICAL":
            return {
                bg: "bg-red-500/15 border-red-500/40 text-red-400",
                dot: "bg-red-400 shadow-[0_0_8px_#ef4444]",
                pill: "text-red-400"
            };
        case "HIGH":
            return {
                bg: "bg-orange-500/15 border-orange-500/40 text-orange-400",
                dot: "bg-orange-400 shadow-[0_0_8px_#f97316]",
                pill: "text-orange-400"
            };
        case "MEDIUM":
            return {
                bg: "bg-amber-500/15 border-amber-500/40 text-amber-400",
                dot: "bg-amber-400 shadow-[0_0_8px_#f59e0b]",
                pill: "text-amber-400"
            };
        default:
            return {
                bg: "bg-emerald-500/15 border-emerald-500/40 text-emerald-400",
                dot: "bg-emerald-400 shadow-[0_0_8px_#10b981]",
                pill: "text-emerald-400"
            };
    }
};

// ─── KPI Stat Card Component ──────────────────────────────────
const KpiCard = ({ icon: Icon, label, value, sub, badge, color = "blue" }) => {
    const colorMap = {
        blue: {
            border: "border-blue-500/20 hover:border-blue-500/40",
            iconBg: "bg-blue-500/10 border-blue-500/30 text-blue-400",
            glow: "group-hover:shadow-[0_0_20px_rgba(59,130,246,0.15)]",
            text: "text-blue-400"
        },
        purple: {
            border: "border-purple-500/20 hover:border-purple-500/40",
            iconBg: "bg-purple-500/10 border-purple-500/30 text-purple-400",
            glow: "group-hover:shadow-[0_0_20px_rgba(139,92,246,0.15)]",
            text: "text-purple-400"
        },
        cyan: {
            border: "border-cyan-500/20 hover:border-cyan-500/40",
            iconBg: "bg-cyan-500/10 border-cyan-500/30 text-cyan-400",
            glow: "group-hover:shadow-[0_0_20px_rgba(6,182,212,0.15)]",
            text: "text-cyan-400"
        },
        emerald: {
            border: "border-emerald-500/20 hover:border-emerald-500/40",
            iconBg: "bg-emerald-500/10 border-emerald-500/30 text-emerald-400",
            glow: "group-hover:shadow-[0_0_20px_rgba(16,185,129,0.15)]",
            text: "text-emerald-400"
        },
        amber: {
            border: "border-amber-500/20 hover:border-amber-500/40",
            iconBg: "bg-amber-500/10 border-amber-500/30 text-amber-400",
            glow: "group-hover:shadow-[0_0_20px_rgba(245,158,11,0.15)]",
            text: "text-amber-400"
        }
    };
    const c = colorMap[color] || colorMap.blue;

    return (
        <div className={`relative bg-[#0F172A]/80 backdrop-blur-xl border ${c.border} rounded-2xl p-5 group transition-all duration-300 ${c.glow} overflow-hidden`}>
            {/* Ambient background glow */}
            <div className={`absolute -right-8 -top-8 w-24 h-24 rounded-full ${c.iconBg} blur-2xl opacity-20 group-hover:opacity-40 transition-opacity pointer-events-none`} />
            
            <div className="flex items-start justify-between gap-3 relative z-10">
                <div className="space-y-2 flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest truncate">{label}</p>
                        {badge && (
                            <span className="text-[9px] font-mono px-1.5 py-0.5 rounded bg-white/[0.05] text-slate-300 border border-white/[0.08]">
                                {badge}
                            </span>
                        )}
                    </div>
                    <p className="text-2xl lg:text-3xl font-black text-slate-100 font-mono tracking-tight leading-none truncate">
                        {typeof value === "number" ? value.toLocaleString() : value}
                    </p>
                    {sub && (
                        <p className="text-[11px] text-slate-400 font-medium truncate flex items-center gap-1.5">
                            {sub}
                        </p>
                    )}
                </div>

                <div className={`w-11 h-11 rounded-xl ${c.iconBg} border flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform duration-300`}>
                    <Icon className="w-5 h-5" />
                </div>
            </div>
        </div>
    );
};

// ─── Custom Recharts Tooltip ──────────────────────────────────
const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    const total = payload.reduce((sum, p) => sum + (typeof p.value === "number" ? p.value : 0), 0);

    return (
        <div className="bg-[#0B0F19]/95 backdrop-blur-xl border border-slate-700/80 rounded-xl px-4 py-3 shadow-[0_10px_30px_rgba(0,0,0,0.6)] text-[11px] min-w-[170px]">
            <p className="text-slate-400 font-bold mb-2 uppercase tracking-widest border-b border-slate-800 pb-1 flex items-center justify-between">
                <span>{label}</span>
                <span className="text-[9px] text-slate-500 font-mono">FLOW BUCKET</span>
            </p>
            <div className="space-y-1.5">
                {payload.map((p, i) => (
                    <div key={i} className="flex items-center justify-between gap-4 font-mono">
                        <span className="flex items-center gap-1.5 text-slate-300">
                            <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: p.color || p.stroke }} />
                            {p.name}:
                        </span>
                        <span className="font-bold text-slate-100">{p.value?.toLocaleString()}</span>
                    </div>
                ))}
            </div>
            {payload.length > 1 && total > 0 && (
                <div className="mt-2 pt-1.5 border-t border-slate-800/80 flex items-center justify-between text-[10px] text-slate-400 font-mono">
                    <span>Total Activity:</span>
                    <span className="font-bold text-blue-400">{total.toLocaleString()}</span>
                </div>
            )}
        </div>
    );
};

// ─── Main Component ───────────────────────────────────────────
export default function NetworkTrafficAnalysis() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    // Initialize from URL search params if present
    const initCountry = searchParams.get("srcCountry") || searchParams.get("country") || "";
    const initCategory = searchParams.get("category") || "all";

    // Data states
    const [overview, setOverview] = useState({
        totalSessions: 0,
        uniqueSrcIPs: 0,
        uniqueDstIPs: 0,
        topCategory: "—",
        topCategoryCount: 0,
        totalErrors: 0,
        totalWarnings: 0,
        cleanTrafficPercent: 100.0
    });
    const [timeline, setTimeline] = useState([]);
    const [protocols, setProtocols] = useState([]);
    const [talkers, setTalkers] = useState([]);
    const [geoData, setGeoData] = useState([]);
    const [analysis, setAnalysis] = useState({
        summary: "Monitoring network baseline flow...",
        riskLevel: "LOW",
        anomalies: [],
        sampleSize: 0,
        errorRate: 0,
        errorCount: 0,
        warnCount: 0,
        topProtocols: [],
        recommendations: []
    });

    // UI & Filter states
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [timeRange, setTimeRange] = useState("24h");
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [category, setCategory] = useState(initCategory);
    const [srcCountry, setSrcCountry] = useState(initCountry);
    const [expandedIp, setExpandedIp] = useState(null);
    const [copiedIp, setCopiedIp] = useState(null);
    const [talkerSearch, setTalkerSearch] = useState("");
    const [timelineMode, setTimelineMode] = useState("area"); // 'area' | 'bar'
    const [autoRefresh, setAutoRefresh] = useState(false);

    // Auto-refresh timer ref
    const autoRefreshTimerRef = useRef(null);

    // Build URL search params for filtering
    const buildParams = useCallback(() => {
        const p = new URLSearchParams();
        if (startDate || endDate) {
            if (startDate) p.append("startDate", startDate);
            if (endDate) p.append("endDate", endDate);
        } else if (timeRange) {
            p.append("timeRange", timeRange);
        }
        if (category && category !== "all") p.append("category", category);
        if (srcCountry.trim()) p.append("srcCountry", srcCountry.trim());
        return p.toString() ? `?${p.toString()}` : "";
    }, [startDate, endDate, timeRange, category, srcCountry]);

    const token = localStorage.getItem("token");
    const headers = token ? { Authorization: `Bearer ${token}` } : {};

    // Fetch all network telemetry data
    const fetchAll = useCallback(async (isSilent = false) => {
        if (!isSilent) setLoading(true);
        setRefreshing(true);
        const qs = buildParams();

        try {
            const [ovRes, tlRes, prRes, tkRes, geoRes, intlRes] = await Promise.all([
                fetch(`${backendURL}/api/network/overview${qs}`, { headers }),
                fetch(`${backendURL}/api/network/traffic-timeline${qs}`, { headers }),
                fetch(`${backendURL}/api/network/protocol-breakdown${qs}`, { headers }),
                fetch(`${backendURL}/api/network/top-talkers${qs}`, { headers }),
                fetch(`${backendURL}/api/network/geo-sessions${qs}`, { headers }),
                fetch(`${backendURL}/api/network/intelligence${qs}`, { headers }),
            ]);

            const safeJson = async (r) => {
                try { return r.ok ? await r.json() : null; }
                catch { return null; }
            };

            const [ov, tl, pr, tk, geo, intl] = await Promise.all([
                safeJson(ovRes), safeJson(tlRes), safeJson(prRes),
                safeJson(tkRes), safeJson(geoRes), safeJson(intlRes),
            ]);

            if (ov) setOverview(ov);
            if (tl) setTimeline(Array.isArray(tl) ? tl : []);
            if (pr) setProtocols(Array.isArray(pr) ? pr : []);
            if (tk) setTalkers(Array.isArray(tk) ? tk : []);
            if (geo) setGeoData(Array.isArray(geo) ? geo : []);
            if (intl) setAnalysis(intl);
        } catch (err) {
            console.error("Network Traffic fetch error:", err);
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    }, [buildParams]);

    useEffect(() => {
        fetchAll();
    }, [fetchAll]);

    // Handle Auto-Refresh interval
    useEffect(() => {
        if (autoRefresh) {
            autoRefreshTimerRef.current = setInterval(() => {
                fetchAll(true);
            }, 30000);
        } else if (autoRefreshTimerRef.current) {
            clearInterval(autoRefreshTimerRef.current);
        }
        return () => {
            if (autoRefreshTimerRef.current) clearInterval(autoRefreshTimerRef.current);
        };
    }, [autoRefresh, fetchAll]);

    // Copy IP Address helper
    const handleCopyIp = (ip, e) => {
        e.stopPropagation();
        navigator.clipboard.writeText(ip);
        setCopiedIp(ip);
        setTimeout(() => setCopiedIp(null), 2000);
    };

    // Filter Talkers by search term
    const filteredTalkers = talkers.filter(t => {
        if (!talkerSearch.trim()) return true;
        const query = talkerSearch.toLowerCase().trim();
        return (
            t.ip?.toLowerCase().includes(query) ||
            t.country?.toLowerCase().includes(query) ||
            t.deviceName?.toLowerCase().includes(query) ||
            t.riskLevel?.toLowerCase().includes(query)
        );
    });

    // Calculate max session in geo for percentage bar
    const maxGeoSessions = geoData.length > 0 ? (geoData[0]?.sessions || 1) : 1;
    const totalGeoSessions = geoData.reduce((acc, g) => acc + (g.sessions || 0), 0);

    // Total Protocol Volume for percentage calculations
    const totalProtocolSessions = protocols.reduce((acc, p) => acc + (p.value || 0), 0) || overview.totalSessions || 1;
    const maxProtocolSession = protocols.length > 0 ? Math.max(...protocols.map(p => p.value || 0)) : 1;

    // CSV Export handler
    const exportCSV = () => {
        const header = "IP Address,Sessions,Errors,Warnings,Country,Device,Risk Level,Last Seen\n";
        const rows = talkers.map(t =>
            `"${t.ip}",${t.sessions},${t.errors || 0},${t.warnings || 0},"${t.country}","${t.deviceName}","${t.riskLevel}","${t.lastSeen ? new Date(t.lastSeen).toISOString() : ""}"`
        );
        const blob = new Blob([header + rows.join("\n")], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.setAttribute("href", url);
        link.setAttribute("download", `QueryTel_Network_Traffic_${new Date().toISOString().slice(0,10)}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    // PDF Print Export handler
    const exportPDF = () => {
        const printWindow = window.open("", "_blank");
        if (!printWindow) return;
        printWindow.document.write(`
            <!DOCTYPE html>
            <html>
            <head>
                <title>QueryTel SOC - Network Traffic Analysis Report</title>
                <style>
                    body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; padding: 32px; color: #0f172a; line-height: 1.5; }
                    .header { border-bottom: 2px solid #3b82f6; padding-bottom: 16px; margin-bottom: 24px; display: flex; justify-content: space-between; align-items: flex-end; }
                    h1 { margin: 0; color: #1e3a8a; font-size: 22px; text-transform: uppercase; }
                    .meta { font-size: 11px; color: #64748b; }
                    .kpi-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px; margin-bottom: 24px; }
                    .kpi-card { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 8px; padding: 12px; }
                    .kpi-title { font-size: 10px; text-transform: uppercase; color: #64748b; font-weight: 700; }
                    .kpi-value { font-size: 20px; font-weight: 800; color: #0f172a; margin-top: 4px; font-family: monospace; }
                    .section-title { font-size: 14px; font-weight: 700; text-transform: uppercase; margin: 24px 0 12px 0; color: #1e293b; border-bottom: 1px solid #cbd5e1; padding-bottom: 6px; }
                    table { width: 100%; border-collapse: collapse; margin-top: 8px; font-size: 11px; }
                    th, td { border: 1px solid #e2e8f0; padding: 8px 10px; text-align: left; }
                    th { background: #f1f5f9; font-weight: 700; text-transform: uppercase; color: #475569; }
                    tr:nth-child(even) { background: #f8fafc; }
                    .badge { display: inline-block; padding: 2px 6px; border-radius: 4px; font-weight: 700; font-size: 9px; text-transform: uppercase; }
                    .badge-critical { background: #fee2e2; color: #991b1b; }
                    .badge-high { background: #ffedd5; color: #9a3412; }
                    .badge-medium { background: #fef3c7; color: #92400e; }
                    .badge-low { background: #dcfce7; color: #166534; }
                    .footer { margin-top: 40px; font-size: 10px; color: #94a3b8; text-align: center; border-top: 1px solid #e2e8f0; padding-top: 12px; }
                </style>
            </head>
            <body>
                <div class="header">
                    <div>
                        <h1>QueryTel SOC — Network Traffic Telemetry</h1>
                        <div class="meta">FortiOS Gateway Flow Analysis · Real-Time Incident Report</div>
                    </div>
                    <div class="meta" style="text-align: right;">
                        <div><strong>Generated:</strong> ${new Date().toLocaleString()}</div>
                        <div><strong>Timeframe:</strong> ${timeRange.toUpperCase()} ${startDate ? `(${startDate} → ${endDate})` : ""}</div>
                    </div>
                </div>

                <div class="kpi-grid">
                    <div class="kpi-card">
                        <div class="kpi-title">Total Sessions</div>
                        <div class="kpi-value">${overview.totalSessions.toLocaleString()}</div>
                    </div>
                    <div class="kpi-card">
                        <div class="kpi-title">Unique Source Endpoints</div>
                        <div class="kpi-value">${overview.uniqueSrcIPs.toLocaleString()}</div>
                    </div>
                    <div class="kpi-card">
                        <div class="kpi-title">Unique Destination IPs</div>
                        <div class="kpi-value">${overview.uniqueDstIPs.toLocaleString()}</div>
                    </div>
                    <div class="kpi-card">
                        <div class="kpi-title">Dominant Category</div>
                        <div class="kpi-value">${overview.topCategory}</div>
                    </div>
                </div>

                <div class="section-title">Security & Anomaly Assessment</div>
                <p style="font-size: 12px; color: #334155; background: #f8fafc; padding: 12px; border-left: 4px solid #3b82f6; border-radius: 4px;">
                    <strong>Security Posture: ${analysis.riskLevel}</strong> — ${analysis.summary}
                </p>

                <div class="section-title">Top Ingress / Egress Talkers (Ranked by Flow Count)</div>
                <table>
                    <thead>
                        <tr>
                            <th>#</th>
                            <th>IP Address</th>
                            <th>Sessions</th>
                            <th>Errors</th>
                            <th>Warnings</th>
                            <th>Country</th>
                            <th>Device / Sensor</th>
                            <th>Risk Status</th>
                        </tr>
                    </thead>
                    <tbody>
                        ${talkers.map((t, idx) => `
                            <tr>
                                <td>${idx + 1}</td>
                                <td style="font-family: monospace; font-weight: 700;">${t.ip}</td>
                                <td>${t.sessions.toLocaleString()}</td>
                                <td>${t.errors || 0}</td>
                                <td>${t.warnings || 0}</td>
                                <td>${t.country}</td>
                                <td>${t.deviceName}</td>
                                <td><span class="badge badge-${(t.riskLevel || "LOW").toLowerCase()}">${t.riskLevel}</span></td>
                            </tr>
                        `).join("")}
                    </tbody>
                </table>

                <div class="footer">
                    CONFIDENTIAL &amp; PROPRIETARY — QueryTel Security Operations Center (SOC)
                </div>
            </body>
            </html>
        `);
        printWindow.document.close();
        printWindow.focus();
        setTimeout(() => {
            printWindow.print();
        }, 500);
    };

    const riskMeta = riskBadgeStyle(analysis.riskLevel);

    // Active filters counter
    const activeFilterCount = (category !== "all" ? 1 : 0) + (srcCountry.trim() ? 1 : 0) + (startDate ? 1 : 0) + (endDate ? 1 : 0);

    const clearAllFilters = () => {
        setStartDate("");
        setEndDate("");
        setCategory("all");
        setSrcCountry("");
        setTimeRange("24h");
    };

    return (
        <div className="min-h-screen bg-[#070B14] text-slate-300 font-sans pb-16 selection:bg-blue-600/40">
            {/* Luminous tactical grid pattern */}
            <div
                className="fixed inset-0 opacity-[0.03] pointer-events-none"
                style={{
                    backgroundImage: "radial-gradient(circle, #3b82f6 1px, transparent 1px)",
                    backgroundSize: "32px 32px"
                }}
            />

            <div className="relative p-4 md:p-6 lg:p-8 space-y-6 max-w-[1720px] mx-auto">

                {/* ── TOP HEADER / COMMAND BAR ── */}
                <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-5 pb-2">
                    <div className="flex items-center gap-4">
                        <div className="relative">
                            <div className="w-13 h-13 rounded-2xl bg-gradient-to-br from-blue-600/20 via-indigo-600/10 to-blue-950/40 border border-blue-500/30 flex items-center justify-center shadow-[0_0_30px_rgba(59,130,246,0.2)] p-3">
                                <Network className="w-7 h-7 text-blue-400 animate-pulse" />
                            </div>
                            <span className="absolute -bottom-1 -right-1 flex h-3.5 w-3.5">
                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                                <span className="relative inline-flex rounded-full h-3.5 w-3.5 bg-emerald-500 border-2 border-[#070B14]" />
                            </span>
                        </div>
                        <div>
                            <div className="flex items-center gap-3">
                                <h1 className="text-2xl lg:text-3xl font-black text-slate-100 uppercase tracking-tight font-sans">
                                    Network Traffic Analysis
                                </h1>
                                <span className="hidden sm:inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[9px] font-black uppercase tracking-wider bg-blue-500/10 border border-blue-500/30 text-blue-400">
                                    <Radio className="w-3 h-3 animate-pulse" /> Live Telemetry
                                </span>
                            </div>
                            <p className="text-xs font-semibold text-slate-400 mt-1 flex items-center gap-2 tracking-wide">
                                <Activity className="w-3.5 h-3.5 text-blue-400" />
                                Real-Time FortiOS Flow Intelligence &amp; Multi-Sensor Packet Analysis
                            </p>
                        </div>
                    </div>

                    {/* Timeframe selector & Actions */}
                    <div className="flex flex-wrap items-center gap-3 w-full lg:w-auto justify-start lg:justify-end">
                        {/* Time Range Presets */}
                        <div className="bg-[#0F172A] border border-white/[0.08] rounded-xl p-1 flex items-center gap-1 shadow-inner">
                            {["1h", "6h", "24h", "7d"].map((range) => (
                                <button
                                    key={range}
                                    onClick={() => {
                                        setTimeRange(range);
                                        setStartDate("");
                                        setEndDate("");
                                    }}
                                    className={`px-3 py-1.5 rounded-lg text-xs font-black uppercase tracking-wider transition-all duration-200 ${
                                        timeRange === range && !startDate && !endDate
                                            ? "bg-blue-600 text-white shadow-[0_0_12px_rgba(59,130,246,0.5)]"
                                            : "text-slate-400 hover:text-slate-200 hover:bg-white/[0.04]"
                                    }`}
                                >
                                    {range}
                                </button>
                            ))}
                        </div>

                        {/* Auto Refresh Toggle */}
                        <button
                            onClick={() => setAutoRefresh(!autoRefresh)}
                            className={`flex items-center gap-2 px-3.5 py-2 rounded-xl border text-xs font-bold transition-all ${
                                autoRefresh
                                    ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-400 shadow-[0_0_15px_rgba(16,185,129,0.15)]"
                                    : "bg-[#0F172A] border-white/[0.08] text-slate-400 hover:text-slate-200"
                            }`}
                            title="Auto-refresh every 30 seconds"
                        >
                            <span className={`w-2 h-2 rounded-full ${autoRefresh ? "bg-emerald-400 animate-ping" : "bg-slate-600"}`} />
                            <span className="text-[11px] font-mono">30s Live</span>
                        </button>

                        {/* Action Buttons */}
                        <button
                            onClick={exportPDF}
                            className="flex items-center gap-2 px-3.5 py-2 bg-[#0F172A] border border-white/[0.08] text-slate-200 rounded-xl hover:bg-[#1E293B] hover:border-white/[0.15] transition-all text-xs font-bold shadow-sm"
                        >
                            <Printer className="w-3.5 h-3.5 text-slate-400" />
                            <span>PDF</span>
                        </button>

                        <button
                            onClick={exportCSV}
                            className="flex items-center gap-2 px-3.5 py-2 bg-[#0F172A] border border-white/[0.08] text-slate-200 rounded-xl hover:bg-[#1E293B] hover:border-white/[0.15] transition-all text-xs font-bold shadow-sm"
                        >
                            <FileText className="w-3.5 h-3.5 text-slate-400" />
                            <span>CSV</span>
                        </button>

                        <button
                            onClick={() => fetchAll(false)}
                            disabled={refreshing}
                            className="flex items-center justify-center p-2.5 bg-blue-600 hover:bg-blue-500 text-white rounded-xl shadow-[0_0_20px_rgba(59,130,246,0.3)] transition-all active:scale-95 disabled:opacity-50"
                            title="Refresh Network Intelligence"
                        >
                            <RotateCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />
                        </button>
                    </div>
                </div>

                {/* ── SECURITY INTELLIGENCE & FLOW POSTURE BANNER ── */}
                <div className={`relative p-5 lg:p-6 rounded-2xl border transition-all duration-300 overflow-hidden shadow-2xl backdrop-blur-xl ${
                    analysis.riskLevel === 'CRITICAL' ? 'bg-gradient-to-r from-red-950/40 via-[#0F172A] to-[#0F172A] border-red-500/30 shadow-[0_0_40px_rgba(239,68,68,0.1)]' :
                    analysis.riskLevel === 'HIGH' ? 'bg-gradient-to-r from-orange-950/40 via-[#0F172A] to-[#0F172A] border-orange-500/30 shadow-[0_0_40px_rgba(249,115,22,0.1)]' :
                    analysis.riskLevel === 'MEDIUM' ? 'bg-gradient-to-r from-amber-950/30 via-[#0F172A] to-[#0F172A] border-amber-500/30 shadow-[0_0_40px_rgba(245,158,11,0.08)]' :
                    'bg-gradient-to-r from-emerald-950/30 via-[#0F172A] to-[#0F172A] border-emerald-500/30 shadow-[0_0_40px_rgba(16,185,129,0.08)]'
                }`}>
                    <div className="flex flex-col lg:flex-row gap-5 items-start justify-between">
                        <div className="flex items-start gap-4 flex-1">
                            <div className={`w-12 h-12 rounded-2xl flex items-center justify-center shrink-0 border ${riskMeta.bg} shadow-lg`}>
                                {analysis.riskLevel === 'CRITICAL' ? <ShieldAlert className="w-6 h-6 text-red-400 animate-bounce" /> :
                                 analysis.riskLevel === 'HIGH' ? <AlertCircle className="w-6 h-6 text-orange-400" /> :
                                 analysis.riskLevel === 'MEDIUM' ? <AlertCircle className="w-6 h-6 text-amber-400" /> :
                                 <ShieldCheck className="w-6 h-6 text-emerald-400" />}
                            </div>

                            <div className="space-y-2 flex-1">
                                <div className="flex flex-wrap items-center gap-3">
                                    <span className={`inline-flex items-center gap-1.5 text-[10px] font-black px-3 py-1 rounded-full border uppercase tracking-widest ${riskMeta.bg}`}>
                                        <span className={`w-1.5 h-1.5 rounded-full ${riskMeta.dot}`} />
                                        {analysis.riskLevel} Flow Risk Status
                                    </span>
                                    <span className="text-[11px] text-slate-400 font-mono flex items-center gap-1.5">
                                        <Clock className="w-3.5 h-3.5 text-slate-500" />
                                        Sample Window: <strong>{analysis.sampleSize ? analysis.sampleSize.toLocaleString() : 1000}</strong> live flows
                                    </span>
                                    {analysis.errorRate !== undefined && (
                                        <span className="text-[11px] text-slate-400 font-mono">
                                            • Error Velocity: <strong className={analysis.errorRate > 5 ? "text-red-400" : "text-emerald-400"}>{analysis.errorRate}%</strong>
                                        </span>
                                    )}
                                </div>

                                <p className="text-base lg:text-lg font-bold text-slate-100 leading-snug tracking-tight">
                                    {analysis.summary}
                                </p>

                                {/* Anomaly Tags */}
                                {analysis.anomalies && analysis.anomalies.length > 0 && (
                                    <div className="flex flex-wrap gap-2 pt-1">
                                        {analysis.anomalies.map((anomaly, idx) => (
                                            <span
                                                key={idx}
                                                className="inline-flex items-center gap-1.5 text-[10px] font-bold bg-[#1E293B]/80 border border-slate-700/60 px-2.5 py-1 rounded-lg text-slate-300 uppercase tracking-wide"
                                            >
                                                <Zap className="w-3 h-3 text-blue-400" />
                                                {anomaly}
                                            </span>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Recommendation Quick Card */}
                        {analysis.recommendations && analysis.recommendations.length > 0 && (
                            <div className="w-full lg:w-96 bg-[#0B0F19]/80 border border-white/[0.08] rounded-xl p-4 shrink-0 space-y-1.5">
                                <p className="text-[10px] font-black text-blue-400 uppercase tracking-widest flex items-center gap-1.5">
                                    <Shield className="w-3.5 h-3.5" /> SOC Action Guidance
                                </p>
                                <p className="text-xs text-slate-300 leading-relaxed font-medium">
                                    {analysis.recommendations[0]}
                                </p>
                            </div>
                        )}
                    </div>
                </div>

                {/* ── ADVANCED TELEMETRY FILTER TOOLBAR ── */}
                <div className="bg-[#0F172A]/90 backdrop-blur-xl p-4 rounded-2xl border border-white/[0.08] flex flex-wrap items-center justify-between gap-4 shadow-xl">
                    <div className="flex flex-wrap items-center gap-3">
                        <div className="flex items-center gap-2 text-slate-400 pr-2 border-r border-slate-800">
                            <Filter className="w-4 h-4 text-blue-400" />
                            <span className="text-[11px] font-black uppercase tracking-wider text-slate-200">Filters</span>
                            {activeFilterCount > 0 && (
                                <span className="w-5 h-5 rounded-full bg-blue-600 text-white text-[10px] font-bold flex items-center justify-center">
                                    {activeFilterCount}
                                </span>
                            )}
                        </div>

                        {/* Date Pickers */}
                        <div className="flex items-center gap-2 bg-[#1E293B]/60 border border-white/[0.06] rounded-xl px-3 py-1.5">
                            <Calendar className="w-3.5 h-3.5 text-slate-400" />
                            <input
                                type="date"
                                className="bg-transparent text-xs font-semibold text-slate-200 outline-none cursor-pointer"
                                value={startDate}
                                onChange={(e) => setStartDate(e.target.value)}
                            />
                            <span className="text-slate-500 text-[10px] font-bold">TO</span>
                            <input
                                type="date"
                                className="bg-transparent text-xs font-semibold text-slate-200 outline-none cursor-pointer"
                                value={endDate}
                                onChange={(e) => setEndDate(e.target.value)}
                            />
                        </div>

                        {/* Protocol / Category Dropdown */}
                        <div className="relative">
                            <select
                                value={category}
                                onChange={(e) => setCategory(e.target.value)}
                                className="bg-[#1E293B]/60 border border-white/[0.06] text-slate-200 text-xs font-bold px-3.5 py-2 rounded-xl outline-none focus:border-blue-500/50 cursor-pointer appearance-none pr-8"
                            >
                                <option value="all">All Traffic Categories</option>
                                <option value="General Traffic">General Traffic</option>
                                <option value="Application Control">Application Control</option>
                                <option value="SSL">SSL / TLS</option>
                                <option value="DNS">DNS</option>
                                <option value="VPN">VPN Gateway</option>
                                <option value="Web Filter">Web Filter</option>
                                <option value="Admin Access">Admin Access</option>
                                <option value="IPS">IPS Telemetry</option>
                                <option value="Antivirus">Antivirus Stream</option>
                            </select>
                            <ChevronDown className="w-3.5 h-3.5 text-slate-400 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                        </div>

                        {/* Source Country Search */}
                        <div className="flex items-center gap-2 bg-[#1E293B]/60 border border-white/[0.06] rounded-xl px-3 py-1.5 focus-within:border-blue-500/50">
                            <MapPin className="w-3.5 h-3.5 text-slate-400" />
                            <input
                                type="text"
                                placeholder="Origin Country (e.g. US, CA, BG)"
                                className="bg-transparent text-xs font-medium text-slate-200 outline-none w-44 placeholder:text-slate-500"
                                value={srcCountry}
                                onChange={(e) => setSrcCountry(e.target.value)}
                                onKeyDown={(e) => e.key === "Enter" && fetchAll()}
                            />
                            {srcCountry && (
                                <button onClick={() => setSrcCountry("")} className="text-slate-500 hover:text-slate-300">
                                    <X className="w-3 h-3" />
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Filter Actions */}
                    <div className="flex items-center gap-2">
                        {activeFilterCount > 0 && (
                            <button
                                onClick={clearAllFilters}
                                className="px-3.5 py-2 text-slate-400 hover:text-slate-200 text-xs font-bold uppercase tracking-wider transition-colors"
                            >
                                Reset All
                            </button>
                        )}
                        <button
                            onClick={() => fetchAll()}
                            disabled={loading}
                            className="px-5 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white rounded-xl font-black text-xs uppercase tracking-widest flex items-center gap-2 transition-all shadow-[0_0_20px_rgba(59,130,246,0.3)] active:scale-95 disabled:opacity-50"
                        >
                            <Activity className="w-3.5 h-3.5" /> Apply Filters
                        </button>
                    </div>
                </div>

                {/* ── 5 HIGH-DENSITY KPI STRIP ── */}
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                    <KpiCard
                        icon={Wifi}
                        label="Total Flow Sessions"
                        value={overview.totalSessions}
                        sub="Ingested Packet Streams"
                        badge="24H"
                        color="blue"
                    />
                    <KpiCard
                        icon={Server}
                        label="Unique Source IPs"
                        value={overview.uniqueSrcIPs}
                        sub="Originating Host Endpoints"
                        badge="NODES"
                        color="purple"
                    />
                    <KpiCard
                        icon={Globe}
                        label="Unique Dest IPs"
                        value={overview.uniqueDstIPs}
                        sub="Target Systems & Gateways"
                        badge="HOSTS"
                        color="cyan"
                    />
                    <KpiCard
                        icon={Layers}
                        label="Dominant Profile"
                        value={overview.topCategory}
                        sub={overview.topCategoryCount ? `${overview.topCategoryCount.toLocaleString()} events (${overview.totalSessions > 0 ? ((overview.topCategoryCount / overview.totalSessions) * 100).toFixed(1) : 0}%)` : "—"}
                        badge="PROTO"
                        color="emerald"
                    />
                    <KpiCard
                        icon={ShieldCheck}
                        label="Stream Integrity"
                        value={`${overview.cleanTrafficPercent || 100}%`}
                        sub={`${overview.totalErrors?.toLocaleString() || 0} errors · ${overview.totalWarnings?.toLocaleString() || 0} warnings`}
                        badge="HEALTH"
                        color={overview.cleanTrafficPercent < 98 ? "amber" : "emerald"}
                    />
                </div>

                {/* ── MAIN WORKSPACE (2 COLUMNS) ── */}
                {loading && !refreshing ? (
                    <div className="flex flex-col items-center justify-center h-96 gap-4 bg-[#0F172A]/40 rounded-3xl border border-white/[0.04]">
                        <div className="relative">
                            <div className="w-14 h-14 border-4 border-blue-500/20 border-t-blue-500 rounded-full animate-spin" />
                            <Network className="w-6 h-6 text-blue-400 absolute inset-0 m-auto animate-pulse" />
                        </div>
                        <p className="text-xs font-black text-slate-400 uppercase tracking-widest animate-pulse">
                            Querying Flow Telemetry &amp; Traffic Baseline...
                        </p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">

                        {/* LEFT COLUMN: Volume Timeline + Protocol Breakdown (8 cols) */}
                        <div className="lg:col-span-8 space-y-6">

                            {/* ── Traffic Volume & Threat Timeline ── */}
                            <div className="bg-[#0F172A]/90 backdrop-blur-xl rounded-3xl border border-white/[0.08] p-6 shadow-xl relative overflow-hidden">
                                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
                                    <div>
                                        <h3 className="text-sm font-black text-slate-100 uppercase tracking-widest flex items-center gap-2.5">
                                            <TrendingUp className="w-4 h-4 text-blue-400" />
                                            Traffic Volume &amp; Telemetry Timeline
                                        </h3>
                                        <p className="text-[11px] text-slate-400 mt-0.5">
                                            Time-series bucket flow telemetry with error and warning overlay
                                        </p>
                                    </div>

                                    {/* Timeline controls */}
                                    <div className="flex items-center gap-3">
                                        <div className="bg-[#1E293B]/70 rounded-xl p-1 border border-white/[0.06] flex items-center gap-1 text-[10px] font-bold">
                                            <button
                                                onClick={() => setTimelineMode("area")}
                                                className={`px-2.5 py-1 rounded-lg uppercase tracking-wider transition-colors ${
                                                    timelineMode === "area" ? "bg-blue-600 text-white" : "text-slate-400 hover:text-slate-200"
                                                }`}
                                            >
                                                Area Curve
                                            </button>
                                            <button
                                                onClick={() => setTimelineMode("bar")}
                                                className={`px-2.5 py-1 rounded-lg uppercase tracking-wider transition-colors ${
                                                    timelineMode === "bar" ? "bg-blue-600 text-white" : "text-slate-400 hover:text-slate-200"
                                                }`}
                                            >
                                                Threat Bars
                                            </button>
                                        </div>

                                        <div className="hidden sm:flex items-center gap-3 text-[10px] font-mono text-slate-400 pl-2 border-l border-slate-800">
                                            <span className="flex items-center gap-1.5">
                                                <span className="w-2.5 h-2.5 rounded bg-blue-500" /> Sessions
                                            </span>
                                            <span className="flex items-center gap-1.5">
                                                <span className="w-2.5 h-2.5 rounded bg-amber-500" /> Warnings
                                            </span>
                                            <span className="flex items-center gap-1.5">
                                                <span className="w-2.5 h-2.5 rounded bg-red-500" /> Errors
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                {timeline.length === 0 ? (
                                    <div className="h-72 flex flex-col items-center justify-center text-slate-500 text-xs font-mono uppercase tracking-widest border border-dashed border-slate-800 rounded-2xl gap-2">
                                        <Activity className="w-8 h-8 text-slate-700" />
                                        <span>No timeline telemetry matching current filter range</span>
                                    </div>
                                ) : (
                                    <div className="h-[280px]">
                                        <ResponsiveContainer width="100%" height="100%">
                                            {timelineMode === "area" ? (
                                                <AreaChart data={timeline} margin={{ top: 10, right: 10, left: -15, bottom: 0 }}>
                                                    <defs>
                                                        <linearGradient id="gradFlowSessions" x1="0" y1="0" x2="0" y2="1">
                                                            <stop offset="0%" stopColor="#3b82f6" stopOpacity={0.4} />
                                                            <stop offset="95%" stopColor="#3b82f6" stopOpacity={0.0} />
                                                        </linearGradient>
                                                        <linearGradient id="gradFlowErrors" x1="0" y1="0" x2="0" y2="1">
                                                            <stop offset="0%" stopColor="#ef4444" stopOpacity={0.4} />
                                                            <stop offset="95%" stopColor="#ef4444" stopOpacity={0.0} />
                                                        </linearGradient>
                                                        <linearGradient id="gradFlowWarnings" x1="0" y1="0" x2="0" y2="1">
                                                            <stop offset="0%" stopColor="#f59e0b" stopOpacity={0.3} />
                                                            <stop offset="95%" stopColor="#f59e0b" stopOpacity={0.0} />
                                                        </linearGradient>
                                                    </defs>
                                                    <CartesianGrid strokeDasharray="3 3" stroke="#1E293B" vertical={false} />
                                                    <XAxis
                                                        dataKey="time"
                                                        tick={{ fill: "#64748B", fontSize: 10, fontWeight: 600 }}
                                                        axisLine={{ stroke: "#1E293B" }}
                                                        tickLine={false}
                                                        interval="preserveStartEnd"
                                                    />
                                                    <YAxis
                                                        tick={{ fill: "#64748B", fontSize: 10, fontWeight: 600 }}
                                                        axisLine={false}
                                                        tickLine={false}
                                                        tickFormatter={(v) => v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v}
                                                    />
                                                    <Tooltip content={<CustomTooltip />} />
                                                    <Area
                                                        type="monotone"
                                                        dataKey="sessions"
                                                        name="Sessions"
                                                        stroke="#3b82f6"
                                                        fill="url(#gradFlowSessions)"
                                                        strokeWidth={2.5}
                                                        dot={false}
                                                        activeDot={{ r: 5, fill: "#3b82f6", stroke: "#fff", strokeWidth: 2 }}
                                                    />
                                                    <Area
                                                        type="monotone"
                                                        dataKey="warnings"
                                                        name="Warnings"
                                                        stroke="#f59e0b"
                                                        fill="url(#gradFlowWarnings)"
                                                        strokeWidth={1.5}
                                                        dot={false}
                                                    />
                                                    <Area
                                                        type="monotone"
                                                        dataKey="errors"
                                                        name="Errors"
                                                        stroke="#ef4444"
                                                        fill="url(#gradFlowErrors)"
                                                        strokeWidth={1.5}
                                                        dot={false}
                                                    />
                                                </AreaChart>
                                            ) : (
                                                <BarChart data={timeline} margin={{ top: 10, right: 10, left: -15, bottom: 0 }}>
                                                    <CartesianGrid strokeDasharray="3 3" stroke="#1E293B" vertical={false} />
                                                    <XAxis
                                                        dataKey="time"
                                                        tick={{ fill: "#64748B", fontSize: 10, fontWeight: 600 }}
                                                        axisLine={{ stroke: "#1E293B" }}
                                                        tickLine={false}
                                                        interval="preserveStartEnd"
                                                    />
                                                    <YAxis
                                                        tick={{ fill: "#64748B", fontSize: 10, fontWeight: 600 }}
                                                        axisLine={false}
                                                        tickLine={false}
                                                        tickFormatter={(v) => v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v}
                                                    />
                                                    <Tooltip content={<CustomTooltip />} />
                                                    <Bar dataKey="sessions" name="Sessions" fill="#3b82f6" radius={[4, 4, 0, 0]} opacity={0.6} />
                                                    <Bar dataKey="warnings" name="Warnings" fill="#f59e0b" radius={[4, 4, 0, 0]} />
                                                    <Bar dataKey="errors" name="Errors" fill="#ef4444" radius={[4, 4, 0, 0]} />
                                                </BarChart>
                                            )}
                                        </ResponsiveContainer>
                                    </div>
                                )}

                                {/* Timeline Summary Footer */}
                                {timeline.length > 0 && (
                                    <div className="mt-4 pt-3 border-t border-slate-800/80 flex flex-wrap items-center justify-between text-[11px] text-slate-400 font-mono gap-3">
                                        <div className="flex items-center gap-4">
                                            <span>Resolution: <strong>{timeRange === "1h" ? "5-min" : timeRange === "6h" ? "15-min" : timeRange === "7d" ? "6-hour" : "30-min"} buckets</strong></span>
                                            <span>Buckets: <strong>{timeline.length}</strong></span>
                                        </div>
                                        <div className="flex items-center gap-4">
                                            <span>Peak Bucket: <strong className="text-blue-400">{Math.max(...timeline.map(t => t.sessions || 0)).toLocaleString()}</strong></span>
                                            <span>Total Filtered: <strong className="text-slate-200">{timeline.reduce((acc, t) => acc + (t.sessions || 0), 0).toLocaleString()}</strong></span>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* ── Protocol & Application Distribution (Redesigned & Balanced) ── */}
                            <div className="bg-[#0F172A]/90 backdrop-blur-xl rounded-3xl border border-white/[0.08] p-6 shadow-xl space-y-5">
                                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                                    <div>
                                        <h3 className="text-sm font-black text-slate-100 uppercase tracking-widest flex items-center gap-2.5">
                                            <Layers className="w-4 h-4 text-purple-400" />
                                            Protocol &amp; Application Distribution
                                        </h3>
                                        <p className="text-[11px] text-slate-400 mt-0.5">
                                            Breakdown of ingested firewall packet flows by service category
                                        </p>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        {category !== "all" && (
                                            <button
                                                onClick={() => setCategory("all")}
                                                className="text-[10px] font-bold text-blue-400 hover:text-blue-300 uppercase px-2 py-1 rounded bg-blue-500/10 border border-blue-500/30"
                                            >
                                                Clear Filter
                                            </button>
                                        )}
                                        <span className="text-[10px] font-mono text-slate-400 font-bold bg-[#1E293B] px-2.5 py-1 rounded-lg border border-white/[0.06]">
                                            {protocols.length} Active Protocols
                                        </span>
                                    </div>
                                </div>

                                {protocols.length === 0 ? (
                                    <div className="h-48 flex items-center justify-center text-slate-500 text-xs font-mono uppercase tracking-widest border border-dashed border-slate-800 rounded-2xl">
                                        No protocol breakdown telemetry available
                                    </div>
                                ) : (
                                    <div className="space-y-4">
                                        {/* ── Visual Proportional Spectrum Bar ── */}
                                        <div className="bg-[#1E293B]/60 p-3 rounded-2xl border border-white/[0.04] space-y-2">
                                            <div className="flex items-center justify-between text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                                                <span>Flow Spectrum Share</span>
                                                <span className="font-mono text-slate-300">{totalProtocolSessions.toLocaleString()} Total Flow Events</span>
                                            </div>
                                            {/* Stacked continuous proportional bar */}
                                            <div className="h-3.5 w-full bg-[#0B0F19] rounded-xl overflow-hidden flex shadow-inner p-0.5 gap-0.5">
                                                {protocols.map((p, idx) => {
                                                    const pct = Math.max(0.5, ((p.value / totalProtocolSessions) * 100));
                                                    const color = PROTOCOL_COLORS[idx % PROTOCOL_COLORS.length];
                                                    return (
                                                        <div
                                                            key={p.name}
                                                            style={{
                                                                width: `${pct}%`,
                                                                backgroundColor: color
                                                            }}
                                                            className="h-full first:rounded-l-lg last:rounded-r-lg transition-all duration-500 hover:opacity-100 opacity-90 cursor-pointer"
                                                            title={`${p.name}: ${p.value.toLocaleString()} flows (${((p.value / totalProtocolSessions) * 100).toFixed(1)}%)`}
                                                            onClick={() => setCategory(category === p.name ? "all" : p.name)}
                                                        />
                                                    );
                                                })}
                                            </div>
                                        </div>

                                        {/* ── Balanced 3-Column Symmetrical Protocol Matrix ── */}
                                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                                            {protocols.map((p, idx) => {
                                                const pctOfTotal = ((p.value / totalProtocolSessions) * 100).toFixed(1);
                                                const widthPct = Math.round((p.value / maxProtocolSession) * 100);
                                                const isSelected = category === p.name;
                                                const color = PROTOCOL_COLORS[idx % PROTOCOL_COLORS.length];

                                                return (
                                                    <div
                                                        key={p.name}
                                                        onClick={() => setCategory(isSelected ? "all" : p.name)}
                                                        className={`p-3.5 rounded-2xl border transition-all duration-200 cursor-pointer group flex flex-col justify-between gap-3 relative overflow-hidden ${
                                                            isSelected
                                                                ? "bg-blue-600/20 border-blue-500 shadow-[0_0_20px_rgba(59,130,246,0.3)] ring-1 ring-blue-500"
                                                                : "bg-[#1E293B]/50 border-white/[0.05] hover:bg-[#1E293B]/90 hover:border-white/[0.12] hover:shadow-lg"
                                                        }`}
                                                    >
                                                        {/* Top row: Rank badge + Name + Percentage pill */}
                                                        <div className="flex items-center justify-between gap-2">
                                                            <div className="flex items-center gap-2 min-w-0">
                                                                <span
                                                                    className="w-5 h-5 rounded-lg flex items-center justify-center text-[10px] font-black font-mono shrink-0 shadow-sm"
                                                                    style={{
                                                                        backgroundColor: `${color}25`,
                                                                        color: color,
                                                                        border: `1px solid ${color}50`
                                                                    }}
                                                                >
                                                                    {idx + 1}
                                                                </span>
                                                                <p className="text-xs font-bold text-slate-100 truncate group-hover:text-white">
                                                                    {p.name}
                                                                </p>
                                                            </div>
                                                            <span
                                                                className="text-[10px] font-mono font-bold px-2 py-0.5 rounded-md border shrink-0"
                                                                style={{
                                                                    backgroundColor: `${color}15`,
                                                                    color: color,
                                                                    borderColor: `${color}30`
                                                                }}
                                                            >
                                                                {pctOfTotal}%
                                                            </span>
                                                        </div>

                                                        {/* Middle row: Total events count */}
                                                        <div className="flex items-baseline justify-between font-mono">
                                                            <span className="text-lg font-black text-slate-100 leading-none">
                                                                {p.value.toLocaleString()}
                                                            </span>
                                                            <span className="text-[10px] text-slate-400 font-sans font-medium">
                                                                flows
                                                            </span>
                                                        </div>

                                                        {/* Bottom row: Clean Progress Meter */}
                                                        <div className="space-y-1">
                                                            <div className="h-1.5 w-full bg-[#0B0F19] rounded-full overflow-hidden">
                                                                <div
                                                                    className="h-full rounded-full transition-all duration-700"
                                                                    style={{
                                                                        width: `${Math.max(4, widthPct)}%`,
                                                                        backgroundColor: color
                                                                    }}
                                                                />
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* RIGHT COLUMN: Top Talkers + Geo Origins (4 cols) */}
                        <div className="lg:col-span-4 space-y-6">

                            {/* ── Top Talkers Ingress / Egress ── */}
                            <div className="bg-[#0F172A]/90 backdrop-blur-xl rounded-3xl border border-white/[0.08] shadow-xl overflow-hidden">
                                <div className="px-5 py-4 border-b border-white/[0.06] bg-[#1E293B]/40 space-y-3">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <h3 className="text-xs font-black text-slate-100 uppercase tracking-widest flex items-center gap-2">
                                                <ShieldAlert className="w-4 h-4 text-orange-400" /> Top Talkers
                                            </h3>
                                            <p className="text-[10px] text-slate-400 font-mono mt-0.5">
                                                High-frequency source IP addresses
                                            </p>
                                        </div>
                                        <span className="text-[10px] font-mono font-bold text-slate-400 bg-[#0F172A] px-2 py-0.5 rounded border border-white/[0.06]">
                                            Top {talkers.length}
                                        </span>
                                    </div>

                                    {/* Search within talkers */}
                                    <div className="relative">
                                        <Search className="w-3.5 h-3.5 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
                                        <input
                                            type="text"
                                            placeholder="Search by IP, device, country..."
                                            value={talkerSearch}
                                            onChange={(e) => setTalkerSearch(e.target.value)}
                                            className="w-full bg-[#0F172A]/80 border border-white/[0.06] rounded-xl pl-8 pr-3 py-1.5 text-xs text-slate-200 placeholder:text-slate-600 outline-none focus:border-blue-500/50"
                                        />
                                        {talkerSearch && (
                                            <button
                                                onClick={() => setTalkerSearch("")}
                                                className="text-slate-500 hover:text-slate-300 absolute right-2.5 top-1/2 -translate-y-1/2"
                                            >
                                                <X className="w-3 h-3" />
                                            </button>
                                        )}
                                    </div>
                                </div>

                                <div className="overflow-y-auto max-h-[380px] custom-scrollbar divide-y divide-white/[0.04]">
                                    {filteredTalkers.length === 0 ? (
                                        <div className="h-40 flex flex-col items-center justify-center text-slate-500 text-xs font-mono uppercase tracking-widest gap-2">
                                            <Server className="w-6 h-6 text-slate-700" />
                                            <span>No top talkers found</span>
                                        </div>
                                    ) : (
                                        filteredTalkers.map((t, idx) => {
                                            const isExpanded = expandedIp === t.ip;
                                            const riskStyle = riskBadgeStyle(t.riskLevel);
                                            const isCopied = copiedIp === t.ip;

                                            return (
                                                <div key={t.ip} className="transition-colors">
                                                    <div
                                                        onClick={() => setExpandedIp(isExpanded ? null : t.ip)}
                                                        className={`px-4 py-3 flex items-center justify-between hover:bg-white/[0.02] cursor-pointer transition-colors ${
                                                            isExpanded ? "bg-white/[0.04]" : ""
                                                        }`}
                                                    >
                                                        <div className="flex items-center gap-3 min-w-0">
                                                            <span className="text-[10px] font-black text-slate-500 font-mono w-4 text-right">
                                                                {idx + 1}
                                                            </span>
                                                            <div className="min-w-0">
                                                                <div className="flex items-center gap-2">
                                                                    <p className="text-xs font-mono font-bold text-slate-100 truncate">
                                                                        {t.ip}
                                                                    </p>
                                                                    <button
                                                                        onClick={(e) => handleCopyIp(t.ip, e)}
                                                                        className="text-slate-500 hover:text-blue-400 p-0.5 rounded transition-colors"
                                                                        title="Copy IP Address"
                                                                    >
                                                                        {isCopied ? (
                                                                            <Check className="w-3 h-3 text-emerald-400" />
                                                                        ) : (
                                                                            <Copy className="w-3 h-3" />
                                                                        )}
                                                                    </button>
                                                                </div>
                                                                <p className="text-[10px] text-slate-400 font-medium truncate flex items-center gap-1.5 mt-0.5">
                                                                    <span className="text-slate-300 font-mono font-bold">{t.sessions.toLocaleString()}</span> flows
                                                                    <span>•</span>
                                                                    <span className="uppercase text-slate-400 font-semibold">{t.country || "Local / Private"}</span>
                                                                </p>
                                                            </div>
                                                        </div>

                                                        <div className="flex items-center gap-2.5 shrink-0 ml-2">
                                                            <span className={`text-[9px] font-black px-2 py-0.5 rounded-full border uppercase tracking-wider ${riskStyle.bg}`}>
                                                                {t.riskLevel}
                                                            </span>
                                                            {isExpanded ? (
                                                                <ChevronUp className="w-4 h-4 text-slate-400" />
                                                            ) : (
                                                                <ChevronDown className="w-4 h-4 text-slate-500" />
                                                            )}
                                                        </div>
                                                    </div>

                                                    {/* Expanded Telemetry Drawer */}
                                                    {isExpanded && (
                                                        <div className="bg-[#0B0F19]/90 border-t border-b border-white/[0.06] p-4 space-y-3">
                                                            <div className="grid grid-cols-2 gap-2.5">
                                                                <div className="bg-[#1E293B]/50 p-2.5 rounded-xl border border-white/[0.04]">
                                                                    <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest block mb-1">
                                                                        Sensor / Gateway
                                                                    </span>
                                                                    <code className="text-[11px] text-blue-400 font-mono font-bold truncate block">
                                                                        {t.deviceName || "FortiGate-Core"}
                                                                    </code>
                                                                </div>

                                                                <div className="bg-[#1E293B]/50 p-2.5 rounded-xl border border-white/[0.04]">
                                                                    <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest block mb-1">
                                                                        Error / Warning Ratio
                                                                    </span>
                                                                    <span className="text-[11px] font-mono font-bold">
                                                                        <span className={t.errors > 0 ? "text-red-400" : "text-slate-400"}>{t.errors || 0} err</span>
                                                                        <span className="text-slate-600 mx-1">/</span>
                                                                        <span className={t.warnings > 0 ? "text-amber-400" : "text-slate-400"}>{t.warnings || 0} warn</span>
                                                                    </span>
                                                                </div>
                                                            </div>

                                                            <div className="flex items-center justify-between gap-3 bg-[#1E293B]/40 p-2.5 rounded-xl border border-white/[0.04] text-[10px]">
                                                                <div className="min-w-0">
                                                                    <span className="text-slate-400 block font-mono truncate">
                                                                        Last seen: {t.lastSeen ? new Date(t.lastSeen).toLocaleTimeString() : "Recent"}
                                                                    </span>
                                                                </div>
                                                                <button
                                                                    onClick={() => navigate(`/logs?search=${encodeURIComponent(t.ip)}`)}
                                                                    className="flex items-center gap-1 px-2.5 py-1 bg-blue-600/20 hover:bg-blue-600 text-blue-300 hover:text-white rounded-lg transition-all text-[10px] font-black uppercase tracking-wider shrink-0"
                                                                >
                                                                    <span>Drill Logs</span>
                                                                    <ArrowUpRight className="w-3 h-3" />
                                                                </button>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })
                                    )}
                                </div>
                            </div>

                            {/* ── Geographic Session Origins ── */}
                            <div className="bg-[#0F172A]/90 backdrop-blur-xl rounded-3xl border border-white/[0.08] shadow-xl overflow-hidden">
                                <div className="px-5 py-4 border-b border-white/[0.06] bg-[#1E293B]/40 flex items-center justify-between">
                                    <div>
                                        <h3 className="text-xs font-black text-slate-100 uppercase tracking-widest flex items-center gap-2">
                                            <Globe className="w-4 h-4 text-emerald-400" /> Geo Session Origins
                                        </h3>
                                        <p className="text-[10px] text-slate-400 font-mono mt-0.5">
                                            Top source countries of inbound traffic
                                        </p>
                                    </div>
                                    <span className="text-[10px] font-mono text-slate-400 bg-[#0F172A] px-2 py-0.5 rounded border border-white/[0.06]">
                                        {geoData.length} Regions
                                    </span>
                                </div>

                                <div className="p-4 space-y-3 overflow-y-auto max-h-[300px] custom-scrollbar">
                                    {geoData.length === 0 ? (
                                        <div className="h-32 flex flex-col items-center justify-center text-slate-500 text-xs font-mono uppercase tracking-widest gap-2">
                                            <Globe className="w-6 h-6 text-slate-700" />
                                            <span>No geographic origin data</span>
                                        </div>
                                    ) : (
                                        geoData.map((g, idx) => {
                                            const pctOfTotal = totalGeoSessions > 0 ? ((g.sessions / totalGeoSessions) * 100).toFixed(1) : 0;
                                            const widthPct = Math.round((g.sessions / maxGeoSessions) * 100);
                                            const isSelected = srcCountry.toUpperCase() === g.country.toUpperCase();

                                            return (
                                                <div
                                                    key={g.country}
                                                    onClick={() => setSrcCountry(isSelected ? "" : g.country)}
                                                    className={`p-2 rounded-xl transition-all cursor-pointer group ${
                                                        isSelected ? "bg-blue-600/20 border border-blue-500/40" : "hover:bg-white/[0.02]"
                                                    }`}
                                                    title={`Click to filter by ${g.country}`}
                                                >
                                                    <div className="flex items-center justify-between text-xs mb-1.5">
                                                        <div className="flex items-center gap-2">
                                                            <span className="text-[10px] font-black text-slate-500 font-mono w-4">
                                                                {idx + 1}
                                                            </span>
                                                            <span className="font-mono font-bold text-slate-100 uppercase tracking-wider px-1.5 py-0.5 rounded bg-white/[0.06] text-[10px]">
                                                                {g.country}
                                                            </span>
                                                            {isSelected && (
                                                                <span className="text-[9px] font-bold text-blue-400 uppercase">Filtered</span>
                                                            )}
                                                        </div>
                                                        <div className="flex items-center gap-2 font-mono text-[11px]">
                                                            <span className="font-bold text-slate-200">{g.sessions.toLocaleString()}</span>
                                                            <span className="text-slate-500 text-[10px]">({pctOfTotal}%)</span>
                                                        </div>
                                                    </div>

                                                    <div className="h-1.5 w-full bg-[#1E293B] rounded-full overflow-hidden">
                                                        <div
                                                            className="h-full rounded-full transition-all duration-500"
                                                            style={{
                                                                width: `${widthPct}%`,
                                                                backgroundColor: PROTOCOL_COLORS[idx % PROTOCOL_COLORS.length]
                                                            }}
                                                        />
                                                    </div>
                                                </div>
                                            );
                                        })
                                    )}
                                </div>
                            </div>

                        </div>
                    </div>
                )}
            </div>

            {/* Custom scrollbar styling */}
            <style>{`
                .custom-scrollbar::-webkit-scrollbar {
                    width: 5px;
                }
                .custom-scrollbar::-webkit-scrollbar-track {
                    background: transparent;
                }
                .custom-scrollbar::-webkit-scrollbar-thumb {
                    background: rgba(59, 130, 246, 0.2);
                    border-radius: 9999px;
                }
                .custom-scrollbar::-webkit-scrollbar-thumb:hover {
                    background: rgba(59, 130, 246, 0.4);
                }
            `}</style>
        </div>
    );
}
